import { createSlice } from "@reduxjs/toolkit";

import {
  applyBulkQuantity,
  defaultQuantitiesFor,
  isSelectableItem,
  lineQuantity,
} from "./printSelectionModel";

// The Print Center selection lives here — never in rendered rows — so it
// survives search, filters, pagination, switching views and leaving the page.
//
// entries[key] = { item, newStock, extra, origin, addedAt, unavailable?, reprintOfJobId?, receiptSources? }
// receiptSources[receiptId] = new-stock labels a "Print X labels" link put on the line.

export const PRINT_SELECTION_VERSION = 1;

const initialState = {
  version: PRINT_SELECTION_VERSION,
  entries: {},
  order: [],
  updatedAt: null,
};

const touch = (state) => {
  state.updatedAt = new Date().toISOString();
};

const clampCount = (value) => {
  const numeric = Math.floor(Number(value));
  return Number.isFinite(numeric) ? Math.max(0, Math.min(9999, numeric)) : 0;
};

const printSelectionSlice = createSlice({
  name: "printSelection",
  initialState,
  reducers: {
    /**
     * Adds items with workflow defaults. Items already selected keep their
     * quantities unless `quantities` names them explicitly. Blocked items are
     * never added (the UI shows why instead).
     */
    selectItems(state, action) {
      const { items, origin, quantities = {}, reprintOfJobId = null } = action.payload;
      (items || []).forEach((item) => {
        if (!isSelectableItem(item)) return;
        const explicit = quantities[item.key];
        const existing = state.entries[item.key];
        if (existing && !explicit) {
          existing.item = item;
          return;
        }
        const defaults = explicit
          ? { newStock: clampCount(explicit.newStock), extra: clampCount(explicit.extra) }
          : defaultQuantitiesFor(item, origin);
        if (existing) {
          Object.assign(existing, defaults, { item });
          return;
        }
        state.entries[item.key] = {
          item,
          ...defaults,
          origin,
          reprintOfJobId,
          addedAt: new Date().toISOString(),
        };
        state.order.push(item.key);
      });
      touch(state);
    },

    deselectItems(state, action) {
      const keys = new Set(action.payload || []);
      keys.forEach((key) => delete state.entries[key]);
      state.order = state.order.filter((key) => !keys.has(key));
      touch(state);
    },

    setLineQuantities(state, action) {
      const { key, newStock, extra } = action.payload;
      const entry = state.entries[key];
      if (!entry) return;
      if (newStock !== undefined) entry.newStock = clampCount(newStock);
      if (extra !== undefined) entry.extra = clampCount(extra);
      touch(state);
    },

    applyBulk(state, action) {
      const bulkAction = action.payload;
      if (bulkAction === "remove_zero") {
        const zero = state.order.filter((key) => lineQuantity(state.entries[key]) <= 0);
        zero.forEach((key) => delete state.entries[key]);
        state.order = state.order.filter((key) => state.entries[key]);
      } else {
        state.order.forEach((key) => {
          Object.assign(state.entries[key], applyBulkQuantity(state.entries[key], bulkAction));
        });
      }
      touch(state);
    },

    /** Fresh server data for selected items; keys that no longer resolve are flagged, not dropped. */
    refreshItems(state, action) {
      const { items = [], missingKeys = [] } = action.payload;
      items.forEach((item) => {
        const entry = state.entries[item.key];
        if (!entry) return;
        entry.item = item;
        entry.unavailable = false;
      });
      missingKeys.forEach((key) => {
        if (state.entries[key]) state.entries[key].unavailable = true;
      });
    },

    /** Line updates planned by `planReceiptMerge` (receipt deep links). */
    applyReceiptMerge(state, action) {
      (action.payload || []).forEach((update) => {
        const existing = state.entries[update.key];
        if (existing) {
          existing.item = update.item;
          existing.unavailable = false;
          existing.newStock = clampCount(update.newStock);
          existing.receiptSources = update.receiptSources;
          return;
        }
        state.entries[update.key] = {
          item: update.item,
          newStock: clampCount(update.newStock),
          extra: clampCount(update.extra),
          origin: update.origin,
          reprintOfJobId: null,
          receiptSources: update.receiptSources,
          addedAt: new Date().toISOString(),
        };
        state.order.push(update.key);
      });
      touch(state);
    },

    clearSelection(state) {
      state.entries = {};
      state.order = [];
      touch(state);
    },

    /** Restores a saved selection (session hydration or "undo" after printing). */
    restoreSelection(state, action) {
      const saved = action.payload;
      if (!saved || saved.version !== PRINT_SELECTION_VERSION) return;
      state.entries = saved.entries || {};
      state.order = (saved.order || []).filter((key) => state.entries[key]);
      state.updatedAt = saved.updatedAt || null;
    },
  },
});

export const {
  selectItems,
  deselectItems,
  setLineQuantities,
  applyBulk,
  refreshItems,
  applyReceiptMerge,
  clearSelection,
  restoreSelection,
} = printSelectionSlice.actions;

export const selectPrintSelection = (state) => state.printSelection || initialState;

export default printSelectionSlice.reducer;
